'use client'

import { ColumnDef } from '@tanstack/react-table'
import dayjs from 'dayjs'

import { DocumentSearchResult } from '@dc-shared-web/hooks/api/document'
import { PlaybookListResponse } from '@dc-shared-web/hooks/api/cl-cs/types'

import { User } from '@/dc-shared-web/src'
import { CrunchesTableActionMenu } from '@/app/(dashboard)'
import {
  canSeeActionMenu,
  CrunchedByCellHelper,
  DocumentCellHelper,
  TeamCellHelper,
} from '@/app/search/columns'
import { getUserVCPermissions } from '@/app/crunches/[id]/InfoPanel/utils'
import { getUploadVersionDisabledState } from '@/app/helpers/versionComparisonHelper'

export const getCrunchesTableColumns = ({
  user,
  folderId,
  currentTable,
  canMoveCrunches,
  playbookData,
  versionCompareDiscoverabilityImprovements,
}: {
  user: User
  folderId?: string
  currentTable: 'dashboard' | 'archived' | 'shared'
  canMoveCrunches: boolean
  playbookData?: PlaybookListResponse
  versionCompareDiscoverabilityImprovements: boolean
}): ColumnDef<DocumentSearchResult>[] => {
  return [
    {
      accessorKey: 'fileName',
      header: 'Crunch name',
      cell: ({ row }) => {
        const showShareInfo =
          row.original.shareCount > 0 &&
          !user._isUnaffiliatedUser &&
          currentTable !== 'shared'

        return (
          <DocumentCellHelper
            document={row.original}
            folderId={folderId}
            showShareInfo={showShareInfo}
            playbookData={playbookData}
          />
        )
      },
      meta: {
        widthClassName: 'tw-flex-[2]',
      },
    },
    {
      accessorKey: 'crunchedBy',
      header: 'Crunched by',
      cell: ({ row }) => <CrunchedByCellHelper document={row.original} />,
    },
    {
      accessorKey: 'teamName',
      header: 'Team',
      enableSorting: false,
      cell: ({ row }) => <TeamCellHelper document={row.original} />,
    },
    {
      accessorKey: 'createdAt',
      header: 'Crunch date',
      cell: ({ row }) => (
        <div data-testid='crunch-date'>
          {dayjs(row.original.createdAt).format('MMM D, YYYY h:mm A')}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      enableHiding: true,
      cell: ({ row }) => {
        if (!canSeeActionMenu(user, row.original)) {
          return null
        }

        const hasPlaybook = Boolean(
          playbookData?.results?.some(
            (playbook) => playbook.crunch_uuid === row.original.uuid
          )
        )

        const versionsLength = row.original.versions?.length || 0
        const hasMoreThan1Version = versionsLength > 0

        const hasVCPermissions = getUserVCPermissions({
          user,
          hasVersionComparison: hasMoreThan1Version,
          teamId: row.original.teamId.toString(),
        })

        const showUploadVersion =
          versionCompareDiscoverabilityImprovements && hasVCPermissions

        const { disabled, tooltip } = getUploadVersionDisabledState({
          status: row.original.status,
          versionsLength,
          hasPlaybook,
        })

        return (
          <div className='tw-text-center'>
            <CrunchesTableActionMenu
              document={row.original}
              canMoveCrunches={!!canMoveCrunches}
              showUploadVersion={showUploadVersion}
              uploadVersionDisabled={disabled}
              uploadVersionTooltip={tooltip}
            />
          </div>
        )
      },
      meta: {
        widthClassName: 'tw-w-24',
      },
    },
  ]
}
